import { useEffect, useState } from "react";
import api, { fmtMXN, fmtDate } from "@/lib/api";
import { Plus, Trash2, Settings2, Receipt, DollarSign } from "lucide-react";
import { toast } from "sonner";
import { StampCfdiModal } from "@/components/StampCfdiModal";

const ST = {
  pendiente: { label: "Pendiente", color: "#f59e0b" },
  parcial: { label: "Parcial", color: "#22d3ee" },
  pagada: { label: "Pagada", color: "#16a34a" },
  cancelada: { label: "Cancelada", color: "#dc2626" },
};
const fmtDT = (iso) => (iso ? new Date(iso).toLocaleString("es-MX", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "");
const RET_PRESETS = [
  { label: "5 AL MILLAR (INSPECCIÓN)", type: "pct", value: 0.5 },
  { label: "CMIC 2 AL MILLAR", type: "pct", value: 0.2 },
  { label: "RET. IVA", type: "pct", value: 0 },
  { label: "RET. ISR", type: "pct", value: 0 },
];

export const ObraInvoicesTab = ({ user, CobroModal }) => {
  const [invoices, setInvoices] = useState([]);
  const [openNew, setOpenNew] = useState(false);
  const [adjFor, setAdjFor] = useState(null);
  const [payFor, setPayFor] = useState(null);
  const [stampFor, setStampFor] = useState(null);
  const canDelete = ["admin", "contador"].includes(user?.role);

  const load = async () => {
    const { data } = await api.get("/client-invoices");
    setInvoices(data);
  };
  useEffect(() => { load(); }, []);

  const uploadXml = async (inv, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/client-invoices/${inv.id}/cfdi-xml`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`XML timbrado cargado · UUID …${(data.uuid_cfdi || "").slice(-8)}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al cargar el XML"); }
  };
  const downloadCfdi = async (inv, kind) => {
    try {
      const res = await api.get(`/client-invoices/${inv.id}/cfdi/${kind}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `cfdi_${folioX(inv)}.${kind}`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error(`No se pudo descargar el ${kind.toUpperCase()}`); }
  };
  const uploadVerification = async (inv, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/client-invoices/${inv.id}/verification-pdf`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("PDF de verificación guardado; se adjuntará al enviar la factura");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al subir la verificación"); }
  };
  const folioX = (inv) => ((inv.sat || {}).serie_folio || inv.folio || "").replace(/ /g, "_");
  const downloadVerification = async (inv) => {
    try {
      const res = await api.get(`/client-invoices/${inv.id}/verification-pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `VERIFICACION_${folioX(inv)}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("No se pudo descargar la verificación"); }
  };
  const downloadIgife = async (inv) => {
    try {
      const res = await api.get(`/client-invoices/${inv.id}/igife/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `FACTURA_${folioX(inv)}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("No se pudo generar el PDF IGIFE"); }
  };
  const emailCfdi = async (inv) => {
    const email = window.prompt(`${inv.emailed_at ? `Reenviar` : `Enviar`} factura ${inv.folio} (PDF IGIFE + XML) por correo.${inv.emailed_at ? `\nÚltimo envío: ${inv.emailed_to} · ${fmtDT(inv.emailed_at)}` : ""}\nDeja vacío para usar el correo registrado del cliente:`, inv.emailed_to || "");
    if (email === null) return;
    try {
      const { data } = await api.post(`/client-invoices/${inv.id}/send-email`, { email: email || null });
      toast.success(`Factura enviada a ${data.sent_to}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al enviar"); }
  };
  const remove = async (inv) => {
    if (!window.confirm(`¿Eliminar la factura ${inv.folio}?`)) return;
    try { await api.delete(`/client-invoices/${inv.id}`); toast.success("Factura eliminada"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  return (
    <div className="space-y-2" data-testid="obra-invoices-tab">
      <div className="flex justify-end">
        <button data-testid="new-obra-invoice-btn" onClick={() => setOpenNew(true)} className="btn-primary px-3 py-2 text-xs uppercase tracking-widest flex items-center gap-2">
          <Plus className="w-4 h-4" /> Facturar obra
        </button>
      </div>
      {invoices.length === 0 && (
        <div className="panel p-8 text-center text-slate-400 text-sm">
          <Receipt className="w-8 h-8 mx-auto mb-2 text-slate-600" />
          Sin facturas de obra. Usa "Facturar obra" para facturar un anticipo o estimación.
        </div>
      )}
      {invoices.map((inv) => {
        const st = ST[inv.status] || ST.pendiente;
        const hasAdj = (inv.discount_total || 0) > 0 || (inv.local_retentions || []).length > 0;
        return (
          <div key={inv.id} className="panel px-4 py-3 flex items-center gap-3 flex-wrap" data-testid={`obra-invoice-${inv.id}`}>
            <span className="font-mono text-sm font-bold text-slate-100">{inv.folio}</span>
            <span className="px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase" style={{ background: `${st.color}22`, color: st.color, border: `1px solid ${st.color}66` }}>{st.label}</span>
            <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm" style={{ background: "rgba(34,211,238,0.1)", color: "var(--cyan)" }}>{inv.invoice_type || "obra"}</span>
            <div className="text-xs text-slate-400 flex-1 min-w-0 truncate">
              {fmtDate(inv.invoice_date)} · <span className="text-slate-300">{inv.contract_number || "—"}</span> · {inv.obra_name || inv.customer_name || inv.concept}
              {inv.uuid_cfdi && <span className="ml-2 font-mono" style={{ color: "#86efac" }}>UUID …{inv.uuid_cfdi.slice(-8)}{inv.cfdi_test_mode ? " · TEST" : ""}</span>}
            </div>
            <div className="text-right">
              <div className="font-mono text-sm font-bold text-slate-100">{fmtMXN(inv.total)}</div>
              {hasAdj && <div className="text-[10px] font-mono" style={{ color: "#c4b5fd" }}>neto {fmtMXN(inv.net_total)}</div>}
              {inv.balance > 0.01 && <div className="text-[10px]" style={{ color: "#f59e0b" }}>saldo {fmtMXN(inv.balance)}</div>}
            </div>
            <div className="flex items-center gap-1 flex-wrap">
              {!inv.uuid_cfdi && inv.status !== "cancelada" && (
                <button data-testid={`stamp-obra-${inv.id}`} onClick={() => setStampFor(inv)} className="btn-primary px-2 py-1 text-[10px] uppercase tracking-widest">⚡ Timbrar</button>
              )}
              {!inv.facturapi_id && inv.status !== "cancelada" && (
                <label data-testid={`upload-xml-obra-${inv.id}`} title={inv.sat ? "Reemplazar XML timbrado" : "Subir XML timbrado por tu PAC (SAT)"}
                  className="btn-ghost px-2 py-1 text-[10px] uppercase tracking-widest cursor-pointer" style={{ color: "#86efac", border: "1px solid #16a34a" }}>
                  {inv.sat ? "XML ✓" : "Subir XML"}
                  <input type="file" accept=".xml,text/xml" className="hidden" onChange={(e) => { uploadXml(inv, e.target.files[0]); e.target.value = ""; }} />
                </label>
              )}
              {inv.uuid_cfdi && (
                <button data-testid={`xml-dl-obra-${inv.id}`} onClick={() => downloadCfdi(inv, "xml")} title="Descargar XML timbrado"
                  className="btn-ghost px-2 py-1 text-[10px] uppercase tracking-widest" style={{ color: "#86efac", border: "1px solid #16a34a" }}>XML</button>
              )}
              {inv.facturapi_id && inv.uuid_cfdi && (
                <button data-testid={`pdf-sat-obra-${inv.id}`} onClick={() => downloadCfdi(inv, "pdf")} title="PDF timbrado (Facturapi)"
                  className="btn-ghost px-2 py-1 text-[10px] uppercase tracking-widest" style={{ color: "#86efac", border: "1px solid #16a34a" }}>PDF SAT</button>
              )}
              {inv.uuid_cfdi && (
                <label data-testid={`verif-upload-${inv.id}`} title={inv.has_verification ? "Reemplazar PDF de verificación SAT" : "Subir PDF de verificación del SAT (se adjunta al enviar)"}
                  className="btn-ghost px-2 py-1 text-[10px] uppercase tracking-widest cursor-pointer" style={{ color: inv.has_verification ? "#86efac" : "var(--text-dim)", border: `1px solid ${inv.has_verification ? "#16a34a" : "var(--border)"}` }}>
                  {inv.has_verification ? "Verif ✓" : "Subir verif."}
                  <input type="file" accept="application/pdf,.pdf" className="hidden" onChange={(e) => { uploadVerification(inv, e.target.files[0]); e.target.value = ""; }} />
                </label>
              )}
              {inv.has_verification && (
                <button data-testid={`verif-dl-${inv.id}`} onClick={() => downloadVerification(inv)} title="Descargar verificación SAT"
                  className="btn-ghost px-2 py-1 text-[10px] uppercase tracking-widest" style={{ color: "#86efac", border: "1px solid #16a34a" }}>⬇ Verif</button>
              )}
              {inv.has_igife && (
                <button data-testid={`igife-pdf-obra-${inv.id}`} onClick={() => downloadIgife(inv)} className="btn-ghost px-2 py-1 text-[10px] uppercase tracking-widest" style={{ color: "#c4b5fd", border: "1px solid #7c3aed" }}>PDF IGIFE</button>
              )}
              {(inv.facturapi_id || inv.sat || inv.has_igife) && (
                <button data-testid={`email-obra-${inv.id}`} onClick={() => emailCfdi(inv)} title={inv.emailed_at ? `Enviada a ${inv.emailed_to}` : "Enviar por correo"}
                  className="btn-ghost px-2 py-1 text-[10px] uppercase tracking-widest" style={{ color: "var(--cyan)", border: "1px solid var(--cyan)" }}>✉ {inv.emailed_at ? "Reenviar" : "Enviar"}</button>
              )}
              {inv.status !== "cancelada" && (
                <button data-testid={`adjust-obra-${inv.id}`} onClick={() => setAdjFor(inv)} title="Descuento y retenciones locales"
                  className="btn-ghost px-2 py-1 text-[10px] uppercase tracking-widest" style={{ color: hasAdj ? "#c4b5fd" : "var(--text-dim)", border: `1px solid ${hasAdj ? "#7c3aed" : "var(--border)"}` }}>
                  <Settings2 className="w-3 h-3 inline" /> Ret/Desc
                </button>
              )}
              {inv.status !== "cancelada" && inv.balance > 0.01 && (
                <button data-testid={`pay-obra-${inv.id}`} onClick={() => setPayFor(inv)} className="btn-ghost px-2 py-1 text-[10px] uppercase" style={{ color: "var(--cyan)", border: "1px solid var(--cyan)" }}>
                  <DollarSign className="w-3 h-3 inline" />Cobro
                </button>
              )}
              {canDelete && !(inv.payments || []).length && (
                <button data-testid={`delete-obra-${inv.id}`} onClick={() => remove(inv)} className="btn-ghost p-1.5" style={{ color: "#fca5a5" }}><Trash2 className="w-3.5 h-3.5" /></button>
              )}
            </div>
            {inv.emailed_at && (
              <div className="w-full text-[10px] font-mono flex items-center gap-2 flex-wrap" data-testid={`email-legend-${inv.id}`} style={{ color: "#86efac" }}>
                ✉ Enviada a {inv.emailed_to} · {fmtDT(inv.emailed_at)}
                {(inv.email_log || []).length > 1 && (
                  <span className="text-slate-500 cursor-help" title={inv.email_log.map((l) => `${l.to} · ${fmtDT(l.at)}${l.by ? ` · por ${l.by}` : ""}`).join("\n")}>
                    · {inv.email_log.length} envíos (ver historial)
                  </span>
                )}
              </div>
            )}
          </div>
        );
      })}
      {openNew && <FacturarObraModal onClose={() => setOpenNew(false)} onSaved={() => { setOpenNew(false); load(); }} />}
      {stampFor && <StampCfdiModal inv={stampFor} onClose={() => setStampFor(null)} onStamped={() => { setStampFor(null); load(); }} />}
      {adjFor && <AjustesModal inv={adjFor} onClose={() => setAdjFor(null)} onSaved={() => { setAdjFor(null); load(); }} />}
      {payFor && CobroModal && <CobroModal inv={payFor} onClose={() => setPayFor(null)} onSaved={() => { setPayFor(null); load(); }} />}
    </div>
  );
};

function FacturarObraModal({ onClose, onSaved }) {
  const [contracts, setContracts] = useState([]);
  const [cid, setCid] = useState("");
  const [ests, setEsts] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/contracts").then(({ data }) => setContracts(data || [])); }, []);
  useEffect(() => {
    if (!cid) { setEsts([]); return; }
    api.get(`/contracts/${cid}/estimaciones`).then(({ data }) => setEsts(data || []));
  }, [cid]);

  const facturarAnticipo = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/contracts/${cid}/client-invoices/from-advance`);
      toast.success(`Factura ${data.folio} creada por ${fmtMXN(data.total)}`);
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    setBusy(false);
  };
  const facturarEst = async (est) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/estimaciones/${est.id}/client-invoice`);
      toast.success(`Factura ${data.folio} creada por ${fmtMXN(data.total)}`);
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    setBusy(false);
  };
  const facturables = ests.filter((e) => ["autorizada", "pagada"].includes(e.status));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(5,8,18,0.85)", backdropFilter: "blur(6px)" }} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-lg panel-strong max-h-[92vh] overflow-y-auto p-5 space-y-3" data-testid="facturar-obra-modal">
        <div className="label-mono">// FACTURAR OBRA · SERIE C</div>
        <div>
          <label className="label-mono block mb-1">Contrato *</label>
          <select value={cid} onChange={(e) => setCid(e.target.value)} className="input-cyber px-3 py-2 text-sm w-full" data-testid="obra-contract-select">
            <option value="" style={{ background: "#0a0f1d" }}>— Selecciona —</option>
            {contracts.map((c) => (
              <option key={c.id} value={c.id} style={{ background: "#0a0f1d" }}>
                {c.contract_number} · {(c.beneficiary || {}).name || c.description?.slice(0, 50)}
              </option>
            ))}
          </select>
        </div>
        {cid && (
          <div className="space-y-2">
            <button disabled={busy} onClick={facturarAnticipo} data-testid="facturar-anticipo-btn" className="btn-primary w-full px-3 py-2 text-xs uppercase tracking-widest disabled:opacity-50">
              $ Facturar anticipo
            </button>
            <div className="label-mono pt-1">// ESTIMACIONES AUTORIZADAS</div>
            {facturables.length === 0 && <div className="text-xs text-slate-500">Sin estimaciones autorizadas pendientes de facturar.</div>}
            {facturables.map((e) => (
              <div key={e.id} className="panel px-3 py-2 flex items-center gap-2">
                <span className="font-mono text-xs text-slate-200 flex-1">EST {e.number} · {fmtMXN(e.net_to_pay ?? e.total_period)}</span>
                <button disabled={busy} onClick={() => facturarEst(e)} data-testid={`facturar-est-${e.id}`} className="btn-ghost px-2 py-1 text-[10px] uppercase tracking-widest" style={{ color: "var(--cyan)", border: "1px solid var(--cyan)" }}>Facturar</button>
              </div>
            ))}
          </div>
        )}
        <div className="flex justify-end pt-2" style={{ borderTop: "1px solid var(--border)" }}>
          <button onClick={onClose} className="btn-ghost px-4 py-2 text-xs uppercase tracking-widest">Cerrar</button>
        </div>
      </div>
    </div>
  );
}

function AjustesModal({ inv, onClose, onSaved }) {
  const d = inv.discount || {};
  const [discountType, setDiscountType] = useState(d.type || "");
  const [discountValue, setDiscountValue] = useState(d.value || "");
  const [rets, setRets] = useState((inv.local_retentions || []).map((r) => ({ label: r.label, type: r.type, value: r.value })));
  const [saving, setSaving] = useState(false);
  const subtotal = Number(inv.subtotal) || Math.round((Number(inv.total) / 1.16) * 100) / 100;

  const calc = (t, v) => (t === "pct" ? Math.round(subtotal * (Number(v) || 0)) / 100 : Number(v) || 0);
  const discTotal = discountType ? calc(discountType, discountValue) : 0;
  const retTotal = rets.reduce((s, r) => s + calc(r.type, r.value), 0);
  const neto = Math.round((Number(inv.total) - discTotal - retTotal) * 100) / 100;

  const submit = async (e) => {
    e.preventDefault();
    const incomplete = rets.filter((r) => r.label && !(Number(r.value) > 0));
    if (incomplete.length) { toast.warning(`Captura el valor de: ${incomplete.map((r) => r.label).join(", ")}`); return; }
    setSaving(true);
    try {
      await api.patch(`/client-invoices/${inv.id}/adjustments`, {
        discount_type: discountType || null,
        discount_value: Number(discountValue) || 0,
        retentions: rets.filter((r) => r.label && Number(r.value) > 0).map((r) => ({ ...r, value: Number(r.value) })),
      });
      toast.success("Retenciones y descuento guardados");
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(5,8,18,0.85)", backdropFilter: "blur(6px)" }} onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="w-full max-w-lg panel-strong max-h-[92vh] overflow-y-auto p-5 space-y-3" data-testid="ajustes-modal">
        <div className="label-mono">// RETENCIONES Y DESCUENTO · {inv.folio}</div>
        <div className="text-xs text-slate-400">Subtotal {fmtMXN(subtotal)} · Total facturado {fmtMXN(inv.total)}. Los % se calculan sobre el subtotal (sin IVA).</div>
        <div className="grid grid-cols-3 gap-2 items-end">
          <div><label className="label-mono block mb-1">Descuento</label>
            <select value={discountType} onChange={(e) => setDiscountType(e.target.value)} className="input-cyber px-2 py-2 text-xs w-full" data-testid="discount-type">
              <option value="" style={{ background: "#0a0f1d" }}>Sin descuento</option>
              <option value="pct" style={{ background: "#0a0f1d" }}>Porcentaje %</option>
              <option value="fixed" style={{ background: "#0a0f1d" }}>Monto fijo $</option>
            </select></div>
          <div><input type="number" step="0.0001" min="0" disabled={!discountType} placeholder={discountType === "pct" ? "%" : "$"} value={discountValue} onChange={(e) => setDiscountValue(e.target.value)} className="input-cyber px-2 py-2 text-xs w-full text-right font-mono" data-testid="discount-value" /></div>
          <div className="text-right text-xs font-mono text-slate-300">− {fmtMXN(discTotal)}</div>
        </div>
        <div className="label-mono">// RETENCIONES LOCALES</div>
        {rets.map((r, idx) => (
          <div key={idx} className="grid grid-cols-12 gap-2 items-center">
            <input className="input-cyber px-2 py-2 text-xs col-span-5" placeholder="Concepto (ej. 5 AL MILLAR)" value={r.label} onChange={(e) => setRets(rets.map((x, i) => i === idx ? { ...x, label: e.target.value } : x))} data-testid={`ret-label-${idx}`} />
            <select className="input-cyber px-1 py-2 text-xs col-span-2" value={r.type} onChange={(e) => setRets(rets.map((x, i) => i === idx ? { ...x, type: e.target.value } : x))}>
              <option value="pct" style={{ background: "#0a0f1d" }}>%</option>
              <option value="fixed" style={{ background: "#0a0f1d" }}>$</option>
            </select>
            <input type="number" step="0.0001" min="0" className="input-cyber px-2 py-2 text-xs col-span-2 text-right font-mono" value={r.value} onChange={(e) => setRets(rets.map((x, i) => i === idx ? { ...x, value: e.target.value } : x))} data-testid={`ret-value-${idx}`} />
            <div className="col-span-2 text-right text-xs font-mono text-slate-400">− {fmtMXN(calc(r.type, r.value))}</div>
            <button type="button" onClick={() => setRets(rets.filter((_, i) => i !== idx))} className="btn-ghost p-1 col-span-1" style={{ color: "#fca5a5" }}><Trash2 className="w-3 h-3" /></button>
          </div>
        ))}
        <div className="flex gap-1 flex-wrap">
          {RET_PRESETS.map((p, pi) => (
            <button type="button" key={p.label} onClick={() => setRets([...rets, { ...p }])} className="btn-ghost px-2 py-1 text-[10px] uppercase tracking-widest" data-testid={`ret-preset-${pi}`}>+ {p.label}</button>
          ))}
          <button type="button" onClick={() => setRets([...rets, { label: "", type: "pct", value: "" }])} className="btn-ghost px-2 py-1 text-[10px] uppercase tracking-widest"><Plus className="w-3 h-3 inline" /> Otra</button>
        </div>
        <div className="text-right text-sm pt-2" style={{ borderTop: "1px solid var(--border)" }}>
          Neto a cobrar: <b className="font-mono" style={{ color: neto >= 0 ? "#86efac" : "#fca5a5" }} data-testid="neto-preview">{fmtMXN(neto)}</b>
        </div>
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="btn-ghost px-4 py-2 text-xs uppercase tracking-widest">Cancelar</button>
          <button type="submit" disabled={saving || neto < 0} data-testid="save-adjustments-btn" className="btn-primary px-5 py-2 text-xs uppercase tracking-widest disabled:opacity-50">{saving ? "Guardando…" : "Guardar"}</button>
        </div>
      </form>
    </div>
  );
}
